'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { loadPortfolioAsync, resetPortfolioAsync, savePortfolioAsync, calcPortfolioStats } from '../utils/paperTradingStorage';
import type { PaperPortfolio } from '../utils/paperTradingStorage';
import PaperTradingHeader from './PaperTradingHeader';
import PortfolioStats from './PortfolioStats';
import TradeExecutionPanel from './TradeExecutionPanel';
import OpenPositionsTable from './OpenPositionsTable';
import TradeHistoryTable from './TradeHistoryTable';

export default function PaperTradingDashboard() {
  const [portfolio, setPortfolio] = useState<PaperPortfolio | null>(null);
  const [priceTick, setPriceTick] = useState(0);

  // Load from Supabase (with localStorage fallback) on mount
  useEffect(() => {
    loadPortfolioAsync().then((p) => setPortfolio(p));
  }, []);

  // Tick prices every 2 seconds to simulate live feed
  useEffect(() => {
    const interval = setInterval(() => {
      setPriceTick((t) => t + 1);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleReset = useCallback(async () => {
    if (window.confirm('Reset portfolio to $100,000 virtual cash? All positions and history will be cleared.')) {
      const fresh = await resetPortfolioAsync();
      setPortfolio(fresh);
    }
  }, []);

  const handlePortfolioChange = useCallback(async (p: PaperPortfolio) => {
    setPortfolio(p);
    await savePortfolioAsync(p);
  }, []);

  if (!portfolio) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const stats = calcPortfolioStats(portfolio);

  return (
    <div className="space-y-5">
      <PaperTradingHeader
        onReset={handleReset}
        totalEquity={stats.totalEquity}
        totalReturn={stats.totalReturn}
        totalReturnPct={stats.totalReturnPct}
      />

      <PortfolioStats
        virtualCash={portfolio.virtualCash}
        openPnL={stats.openPnL}
        realizedPnL={stats.realizedPnL}
        winRate={stats.winRate}
        totalTrades={portfolio.tradeHistory.length}
        openPositions={portfolio.positions.length}
      />

      <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-5">
        {/* Left: Trade execution */}
        <TradeExecutionPanel
          portfolio={portfolio}
          onPortfolioChange={handlePortfolioChange}
          priceTick={priceTick}
        />

        {/* Right: Positions + History */}
        <div className="space-y-5">
          <OpenPositionsTable
            portfolio={portfolio}
            onPortfolioChange={handlePortfolioChange}
            priceTick={priceTick}
          />
          <TradeHistoryTable trades={portfolio.tradeHistory} />
        </div>
      </div>
    </div>
  );
}
